import React from 'react';
import { Brain, ArrowUp, Users, Globe, Award, Shield } from 'lucide-react';
import { Card } from './ui/card';

const ApproachSection = () => {
  const pillars = [
    {
      icon: Award,
      title: "Science",
      subtitle: "Accréditation Professionnelle",
      description: "L'accréditation ICF et la norme internationale EN ISO/IEC 17024. Une déontologie stricte, une confidentialité absolue et des méthodes dont l'efficacité est validée.",
      color: "warm-sage"
    },
    {
      icon: Users,
      title: "Système",
      subtitle: "Votre Écosystème comme Levier",
      description: "Vous n'êtes pas une entité isolée. Nous cartographions vos interactions pour identifier les véritables leviers de changement avec des outils comme 'L'Équipe Intérieure'.",
      color: "soft-taupe"
    },
    {
      icon: Globe,
      title: "Culture",
      subtitle: "Votre Super-Pouvoir",
      description: "Je ne vous demanderai jamais de renier votre héritage pour réussir. Nous décodons ensemble les codes implicites de vos différents mondes pour naviguer avec aisance.",
      color: "rich-chocolate"
    }
  ];

  return (
    <section id="approche" className="py-12 bg-cream-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="heading-fluid font-serif font-light text-charcoal mb-4 tracking-wide">
            L'Approche Mind'UP
          </h2>
          <p className="subhead-fluid text-warm-gray max-w-3xl mx-auto">
            La Science de l'Âme le Système de la Vie
          </p>
          <div className="mt-6 w-24 h-1 bg-warm-sage mx-auto rounded-full" />
        </div>

        {/* Philosophy Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center mb-10">
          <div>
            <h3 className="text-2xl md:text-3xl font-light text-charcoal mb-6 tracking-wide">
              La Philosophie : Mind → Up
            </h3>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-warm-sage p-3 rounded-full flex-shrink-0 mt-1">
                  <Brain className="h-6 w-6 text-cream-white" />
                </div>
                <div>
                  <h4 className="text-xl font-medium text-charcoal mb-3">Mind : Le Territoire Intérieur</h4>
                  <p className="text-warm-gray leading-relaxed">
                    Nous apprivoisons le mental. Nous identifions les croyances limitantes héritées,
                    les conflits intérieurs et les blocages qui parasitent votre prise de décision.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-soft-taupe p-3 rounded-full flex-shrink-0 mt-1">
                  <ArrowUp className="h-6 w-6 text-charcoal" />
                </div>
                <div>
                  <h4 className="text-xl font-medium text-charcoal mb-3">Up : L'Élévation en Action</h4>
                  <p className="text-warm-gray leading-relaxed">
                    Nous passons de la compréhension à la création. Nous définissons une boussole personnelle
                    pour renforcer votre résilience et incarner la version la plus alignée de vous-même.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1614786269829-d24616faf56d"
              alt="Femme professionnelle confiante"
              className="w-full h-96 object-cover rounded-lg shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 to-transparent rounded-lg" />
          </div>
        </div>

        {/* Three Pillars */}
        <div>
          <h3 className="text-2xl md:text-3xl font-light text-charcoal text-center mb-3">
            Une Architecture Humaine de 3 Piliers
          </h3>
          <p className="text-base md:text-lg text-warm-gray text-center mb-12 max-w-2xl mx-auto">
            Science, Système et Culture
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {pillars.map((pillar, index) => {
              const IconComponent = pillar.icon;
              const bgColorClass = index === 0 ? 'bg-warm-sage' : index === 1 ? 'bg-soft-taupe' : 'bg-rich-chocolate';
              return (
                <Card key={index} className="p-8 bg-cream-white border-soft-gray hover:shadow-xl transition-all duration-300 group rounded-2xl">
                  <div className={`${bgColorClass} p-4 rounded-full w-fit mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="h-8 w-8 text-cream-white" />
                  </div>
                  <h4 className="text-2xl font-medium text-charcoal text-center mb-2">
                    {pillar.title}
                  </h4>
                  <h5 className="text-lg text-warm-sage text-center mb-4 font-medium">
                    {pillar.subtitle}
                  </h5>
                  <p className="text-warm-gray leading-relaxed text-center">
                    {pillar.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Methodology */}
        <div className="mt-12 bg-soft-gray/50 p-8 md:p-12 rounded-lg">
          <h3 className="text-2xl font-light text-charcoal text-center mb-8">
            Comment ? Aide-toi à T'Aider Toi-Même
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <p className="text-base md:text-lg text-charcoal mb-6 leading-relaxed">
                Tu possèdes déjà toutes les réponses. Mon rôle ? T'accompagner à les découvrir,
                à faire confiance à ta sagesse intérieure, celle qui marie admirablement tes héritages.
              </p>
              <div className="flex items-center gap-4">
                <div className="bg-warm-sage text-cream-white px-6 py-3 rounded-full font-medium">
                  80% Toi
                </div>
                <span className="text-warm-gray">+</span>
                <div className="bg-soft-taupe text-charcoal px-6 py-3 rounded-full font-medium">
                  20% Moi
                </div>
              </div>
            </div>
            <div className="text-center">
              <Shield className="h-16 w-16 text-warm-sage mx-auto mb-4" />
              <p className="text-warm-gray">
                Tu es l'experte de ta vie. Je suis la facilitatrice qui t'aide à voir
                ce que tu ne peux pas voir seule.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;
